// Real user profile system with localStorage persistence

export interface UserAchievement {
    id: string;
    icon: string;
    title: string;
    titleRu: string;
    unlocked: boolean;
    unlockedDate?: string;
}

export interface UserStats {
    puzzlesCompleted: number;
    puzzlesCompletedByCategory: Record<string, number>;
    completedPuzzleIds: Set<string>;
    totalPlayTime: number;
    streakDays: number;
    lastPlayDate: string;
}

export interface UserProfile {
    id: string;
    name: string;
    nameRu: string;
    level: number;
    xp: number;
    coins: number;
    stats: UserStats;
    achievements: UserAchievement[];
    joinDate: string;
    lastUpdated: string;
}

const STORAGE_KEY = 'puzzzled_user_profile';
const DEFAULT_ACHIEVEMENTS: UserAchievement[] = [
    { id: '1', icon: '🪆', title: 'Russian Expert', titleRu: 'Русский эксперт', unlocked: false },
    { id: '2', icon: '🏔️', title: 'Nature Lover', titleRu: 'Любитель природы', unlocked: false },
    { id: '3', icon: '🐾', title: 'Animal Friend', titleRu: 'Друг животных', unlocked: false },
    { id: '4', icon: '🔥', title: '3 Day Streak', titleRu: '3 дня подряд', unlocked: false },
    { id: '5', icon: '🔥🔥', title: '7 Day Streak', titleRu: '7 дней подряд', unlocked: false },
    { id: '6', icon: '👑', title: 'Expert Solver', titleRu: 'Мастер эксперт', unlocked: false },
    { id: '7', icon: '💎', title: '500 XP Earned', titleRu: '500 XP заработано', unlocked: false },
    { id: '8', icon: '🌟', title: 'All Categories', titleRu: 'Все категории', unlocked: false },
];

const DEFAULT_PROFILE: UserProfile = {
    id: 'user_' + Date.now(),
    name: 'Puzzle Lover',
    nameRu: 'Любитель пазлов',
    level: 1,
    xp: 0,
    coins: 0,
    stats: {
        puzzlesCompleted: 0,
        puzzlesCompletedByCategory: {},
        completedPuzzleIds: new Set(),
        totalPlayTime: 0,
        streakDays: 0,
        lastPlayDate: new Date().toISOString(),
    },
    achievements: DEFAULT_ACHIEVEMENTS,
    joinDate: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
};

// Get current user profile from localStorage
export const getUserProfile = (): UserProfile => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            // Return a fresh copy of default profile with a new Set
            return {
                ...DEFAULT_PROFILE,
                stats: {
                    ...DEFAULT_PROFILE.stats,
                    completedPuzzleIds: new Set(),
                },
            };
        }
        const profile = JSON.parse(stored);
        // Convert completedPuzzleIds array back to Set if it was stored
        if (Array.isArray(profile.stats.completedPuzzleIds)) {
            profile.stats.completedPuzzleIds = new Set(profile.stats.completedPuzzleIds);
        } else if (!profile.stats.completedPuzzleIds || typeof profile.stats.completedPuzzleIds !== 'object') {
            profile.stats.completedPuzzleIds = new Set();
        }
        return profile;
    } catch (error) {
        console.error('Error loading user profile:', error);
        return {
            ...DEFAULT_PROFILE,
            stats: {
                ...DEFAULT_PROFILE.stats,
                completedPuzzleIds: new Set(),
            },
        };
    }
};

// Save user profile to localStorage
export const saveUserProfile = (profile: UserProfile): void => {
    profile.lastUpdated = new Date().toISOString();
    // Convert Set to Array for JSON serialization
    const profileToSave = {
        ...profile,
        stats: {
            ...profile.stats,
            completedPuzzleIds: Array.from(profile.stats.completedPuzzleIds),
        },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profileToSave));
};

// Add XP to user (triggers level ups and achievements)
export const addXP = (amount: number, categoryId?: string): void => {
    const profile = getUserProfile();
    const oldLevel = profile.level;

    profile.xp += amount;
    profile.coins += Math.floor(amount / 5); // 1 coin per 5 XP

    // Level up every 1000 XP
    profile.level = Math.floor(profile.xp / 1000) + 1;

    // Check for achievements
    checkAchievements(profile, oldLevel, categoryId);

    saveUserProfile(profile);
};

// Mark puzzle as completed (track individual puzzle completions)
export const completePuzzle = (puzzleId: string, categoryId: string, difficulty: 'easy' | 'medium' | 'hard' | 'expert', xpReward: number, coinReward: number): void => {
    const profile = getUserProfile();
    const isFirstCompletion = !profile.stats.completedPuzzleIds.has(puzzleId);

    // Only count as completion if it's the first time
    if (isFirstCompletion) {
        profile.stats.completedPuzzleIds.add(puzzleId);
        profile.stats.puzzlesCompleted += 1;
        profile.stats.puzzlesCompletedByCategory[categoryId] = (profile.stats.puzzlesCompletedByCategory[categoryId] || 0) + 1;

        // Check and update streak
        updateStreak(profile);
    }

    profile.stats.lastPlayDate = new Date().toISOString();

    // Always add XP (players can replay for more XP but only count completion once)
    addXP(xpReward, categoryId);
};

// Update daily streak
const updateStreak = (profile: UserProfile): void => {
    const lastPlayDate = new Date(profile.stats.lastPlayDate);
    const today = new Date();
    const diffTime = today.getTime() - lastPlayDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
        profile.stats.streakDays += 1;
    } else if (diffDays > 1) {
        profile.stats.streakDays = 1;
    }
    // Same day, streak continues
};

// Check and unlock achievements
const checkAchievements = (profile: UserProfile, oldLevel: number, categoryId?: string): void => {
    // Russian Expert (5+ Russian puzzles)
    if ((profile.stats.puzzlesCompletedByCategory['russia'] || 0) >= 5) {
        unlockAchievement(profile, '1');
    }

    // Nature Lover (5+ Nature puzzles)
    if ((profile.stats.puzzlesCompletedByCategory['nature'] || 0) >= 5) {
        unlockAchievement(profile, '2');
    }

    // Animal Friend (5+ Animal puzzles)
    if ((profile.stats.puzzlesCompletedByCategory['animals'] || 0) >= 5) {
        unlockAchievement(profile, '3');
    }

    // 3 Day Streak
    if (profile.stats.streakDays >= 3) {
        unlockAchievement(profile, '4');
    }

    // 7 Day Streak
    if (profile.stats.streakDays >= 7) {
        unlockAchievement(profile, '5');
    }

    // Expert Solver (10+ puzzles)
    if (profile.stats.puzzlesCompleted >= 10) {
        unlockAchievement(profile, '6');
    }

    // 500 XP Earned
    if (profile.xp >= 500) {
        unlockAchievement(profile, '7');
    }

    // All Categories (1+ puzzle in each category)
    const completedCategories = Object.keys(profile.stats.puzzlesCompletedByCategory).filter(
        cat => profile.stats.puzzlesCompletedByCategory[cat] > 0
    ).length;
    if (completedCategories >= 6) {
        unlockAchievement(profile, '8');
    }
};

// Unlock a specific achievement
const unlockAchievement = (profile: UserProfile, achievementId: string): void => {
    const achievement = profile.achievements.find(a => a.id === achievementId);
    if (achievement && !achievement.unlocked) {
        achievement.unlocked = true;
        achievement.unlockedDate = new Date().toISOString();
    }
};

// Reset profile (for testing)
export const resetProfile = (): void => {
    localStorage.removeItem(STORAGE_KEY);
};

// Export for testing
export const getDefaultProfile = (): UserProfile => {
    return JSON.parse(JSON.stringify(DEFAULT_PROFILE));
};
